/**
 * OOS Issues List Component
 *
 * Displays filtered list of Out-of-Stock issues with search/filter bar.
 *
 * BLU-448: All filtering, grouping, sorting, and pagination is server-side.
 * The legacy client-side path capped at 500 rows and silently hid old issues
 * on any location with > 500 open issues — search included.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, PackageX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/loading/PageLoader';
import { useOosIssueGroups } from '../hooks/useProductionIssues';
import { useOosFilters } from '../hooks/useOosFilters';
import { OosFilters } from './OosFilters';
import { IssueCard } from './IssueCard';
import type { ProductionIssue, OosFilterState } from '../types';

const PAGE_SIZE = 25;
const CANDIDATE_CAP = 20000;

interface OOSIssuesListProps {
  locationId: string;
  onViewDetails: (issueId: string, issue?: ProductionIssue) => void;
  /** Seeds the search box (the `?search=` deep link); Reset clears it. */
  initialSearch?: string;
}

export function OOSIssuesList({ locationId, onViewDetails, initialSearch }: OOSIssuesListProps) {
  const { t } = useTranslation('production');
  const [page, setPage] = useState(1);
  const listTopRef = useRef<HTMLDivElement>(null);

  const { filters, rawFilters, updateFilter, resetFilters } = useOosFilters({ initialSearch });

  const offset = (page - 1) * PAGE_SIZE;
  const { groups, total, truncated, isLoading, isFetching } = useOosIssueGroups(
    locationId,
    filters,
    { limit: PAGE_SIZE, offset }
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  // Scroll to top of the list whenever the user changes filters or pages.
  // The list lives below a tall filter bar; without this, a coordinator
  // paginating or refining filters mid-scroll loses orientation.
  useEffect(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page, filters]);

  const handleFilterChange = <K extends keyof OosFilterState>(
    key: K,
    value: OosFilterState[K]
  ) => {
    setPage(1);
    updateFilter(key, value);
  };

  const handleReset = () => {
    setPage(1);
    resetFilters();
  };

  // Empty-state branch: distinguish "no issues at all" from
  // "filters returned no matches" so the copy is actionable. Includes the
  // contacted filter (BLU-448 review).
  const hasActiveFilters =
    filters.status !== 'all' ||
    filters.source !== 'all' ||
    filters.ageFilter !== 'all' ||
    filters.contactedFilter !== 'all' ||
    filters.skuSearch !== '' ||
    filters.search !== '';

  if (isLoading) {
    return <PageLoader variant="production" pool="issues" />;
  }

  return (
    <div ref={listTopRef}>
      <OosFilters
        filters={rawFilters}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
        resultCount={total}
        hasActiveFilters={hasActiveFilters}
      />

      {/* aria-live announces filtered result count for screen readers so they
          hear what's actually shown, not the global counts that the page
          header announces. polite — never interrupts. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {t('issues.oos.resultsAria', { count: total })}
      </div>

      {/* Truncation banner — backend hit the 20k candidate cap. Surfaces the
          condition so operators know to narrow before trusting "no results". */}
      {truncated && (
        <div
          role="status"
          className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{t('issues.oos.truncatedTitle')}</p>
            <p className="text-amber-800/90 dark:text-amber-200/80">
              {t('issues.oos.truncatedBody', { cap: CANDIDATE_CAP.toLocaleString() })}
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      {total === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <PackageX className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
          <p className="mt-4 text-lg font-medium">
            {hasActiveFilters ? t('issues.oos.noMatching') : t('issues.oos.noIssues')}
          </p>
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters
              ? t('issues.oos.tryAdjustingFilters')
              : t('issues.oos.willAppearHere')}
          </p>
        </div>
      ) : (
        <div
          className={`space-y-4 transition-opacity ${
            isFetching ? 'opacity-60' : 'opacity-100'
          }`}
          aria-busy={isFetching}
        >
          {groups.map((group) => (
            <IssueCard
              key={group.prod_order_id}
              group={group}
              prodOrderId={group.prod_order_id}
              onViewDetails={(id) => onViewDetails(id, group.primaryIssue)}
            />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('issues.pagination.showing', {
                  from: offset + 1,
                  to: Math.min(offset + PAGE_SIZE, total),
                  total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t('issues.pagination.page', { current: safePage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t('issues.pagination.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t('issues.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
